import React, { useEffect, useState, useCallback } from "react";
import { Mail, Search, CheckCircle2, XCircle } from "lucide-react";
import { Card } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import api from "@/lib/api";
import { EmptyState } from "@/components/PageHeader";

const fmtDT = (v) => v ? new Date(v).toLocaleString("en-US", { timeZone: "America/Chicago", year: "numeric", month: "short", day: "numeric", hour: "numeric", minute: "2-digit", timeZoneName: "short" }) : "—";

function StatusPill({ status }) {
  const failed = status === "failed";
  return (
    <span className={`inline-flex items-center gap-1 rounded-full px-2 py-0.5 text-xs font-semibold ${failed ? "bg-red-100 text-red-700" : "bg-emerald-100 text-emerald-700"}`} data-testid={`email-status-${status || "delivered"}`}>
      {failed ? <XCircle className="h-3 w-3" /> : <CheckCircle2 className="h-3 w-3" />}
      {failed ? "Failed" : "Delivered"}
    </span>
  );
}

export default function EmailLog() {
  const [data, setData] = useState({ items: [], total: 0, page: 1, page_size: 30 });
  const [q, setQ] = useState("");
  const [status, setStatus] = useState("all");
  const [page, setPage] = useState(1);

  const load = useCallback(async () => {
    const params = new URLSearchParams({ page: String(page), page_size: "30" });
    if (q.trim()) params.set("q", q.trim());
    if (status !== "all") params.set("status", status);
    const { data } = await api.get(`/admin/email-logs?${params.toString()}`);
    setData(data);
  }, [page, q, status]);
  useEffect(() => { const t = setTimeout(load, q ? 350 : 0); return () => clearTimeout(t); }, [load, q]);

  const totalPages = Math.max(1, Math.ceil(data.total / data.page_size));

  return (
    <div data-testid="email-log-panel">
      <div className="mb-4 flex flex-wrap items-center gap-3">
        <div className="relative flex-1 min-w-[220px] max-w-sm">
          <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-slate-400" />
          <Input value={q} onChange={(e) => { setPage(1); setQ(e.target.value); }} placeholder="Search recipient, email, subject or initiator"
            className="pl-9" data-testid="email-log-search" />
        </div>
        <Select value={status} onValueChange={(v) => { setPage(1); setStatus(v); }}>
          <SelectTrigger className="w-40" data-testid="email-log-status-filter"><SelectValue /></SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All statuses</SelectItem>
            <SelectItem value="delivered">Delivered</SelectItem>
            <SelectItem value="failed">Failed</SelectItem>
          </SelectContent>
        </Select>
      </div>
      {data.items.length === 0 ? (
        <EmptyState title="No emails yet" description="Every email the system sends will be recorded here with its delivery status." testId="email-log-empty" />
      ) : (
        <Card className="border-slate-200 shadow-sm overflow-x-auto" data-testid="email-log-card">
          <Table data-testid="email-log-table">
            <TableHeader>
              <TableRow className="bg-slate-50">
                {["Initiated by", "Recipient", "Email address", "Reason", "Sent", "Status"].map((h) => (
                  <TableHead key={h} className="text-xs font-bold uppercase tracking-wide text-slate-500">{h}</TableHead>
                ))}
              </TableRow>
            </TableHeader>
            <TableBody>
              {data.items.map((a) => (
                <TableRow key={a.id} data-testid={`email-log-row-${a.id}`}>
                  <TableCell className="text-sm font-medium text-slate-800">{a.email_initiator || a.acting_user_name || "System"}</TableCell>
                  <TableCell className="text-sm text-slate-700" data-testid={`email-log-recipient-${a.id}`}>{a.email_recipient_name || "—"}</TableCell>
                  <TableCell className="text-xs text-slate-500">{a.email_recipient || "—"}</TableCell>
                  <TableCell className="max-w-xs text-sm text-slate-600">{a.email_subject || a.change_summary}</TableCell>
                  <TableCell className="whitespace-nowrap text-xs text-slate-500">{fmtDT(a.timestamp)}</TableCell>
                  <TableCell><StatusPill status={a.email_status} /></TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </Card>
      )}
      <div className="mt-4 flex items-center justify-between">
        <span className="text-xs text-slate-500" data-testid="email-log-page-label">Page {data.page} of {totalPages} · {data.total} emails</span>
        {totalPages > 1 && (
          <div className="flex gap-2">
            <Button variant="outline" size="sm" className="rounded-full" disabled={page <= 1} onClick={() => setPage((p) => p - 1)} data-testid="email-log-prev">Prev</Button>
            <Button variant="outline" size="sm" className="rounded-full" disabled={page >= totalPages} onClick={() => setPage((p) => p + 1)} data-testid="email-log-next">Next</Button>
          </div>
        )}
      </div>
    </div>
  );
}
